/**
 * The eight bosses (Design Bible §9.3, FS-08 R22 and §4.7). Each is a composition of the ordinary
 * twelve hazard kinds — a boss adds choreography, never a new mechanic (R23).
 *
 * The comments record each encounter's **fairness argument** (R32): the specific reason a competent
 * player survives each phase. A phase whose survivability is asserted rather than argued is not
 * shippable, so where the spec's own arithmetic found a phase too tight (Mandelbrot ph.3, Leviathan
 * ph.3) the mitigation named there is implemented here.
 */
import { dsin } from '../../core/math-det';
import type { BossDef } from './types';

const TAU = Math.PI * 2;

/** Convert a "clock position" 0..1 around the tunnel into θ. */
function ring(t: number): number {
  return ((t % 1) + 1) % 1 * TAU;
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. The Oscillator — Sector 1, Sunset Boulevard (118 BPM)
// The tutorial boss: teaches that a hazard's shape is readable and its node findable.
// Fairness: only kinds seen for a whole sector; phase 4's nodes are *aligned*, so the answer is to
// hold one θ — the boss teaches its own solution. Node width never below 1.4 × MIN_NODE_WIDTH.
// ─────────────────────────────────────────────────────────────────────────────
export const OSCILLATOR: BossDef = {
  id: 'oscillator',
  name: 'THE OSCILLATOR',
  sector: 1,
  phases: [
    {
      name: 'Fundamental',
      bars: 8,
      intensity: 0.7,
      onBeat(c) {
        if (c.beatInBar % 2 === 0) c.emit('SineRibbon', ring(0.25), 0.7, { nodeScale: 1.6 });
      },
    },
    {
      name: 'Harmonic',
      bars: 8,
      intensity: 0.8,
      onBeat(c) {
        const node = ring(0.25 + dsin(c.beat * 0.6) * 0.12);
        c.emit(c.beat % 2 === 0 ? 'SineRibbon' : 'StandingWave', node, 0.8, { nodeScale: 1.5 });
      },
    },
    {
      name: 'Detune',
      bars: 8,
      intensity: 0.88,
      onBeat(c) {
        // Two ribbons offset half a beat — same node, so the pair reads as one problem.
        const node = ring(0.25 + dsin(c.beat * 0.9) * 0.16);
        c.emit('SineRibbon', node, 0.88, { nodeScale: 1.45 });
        if (c.beatInBar % 2 === 1) c.emit('SineRibbon', node, 0.84, { nodeScale: 1.45 });
      },
    },
    {
      name: 'Unison',
      bars: 8,
      intensity: 0.95,
      onBeat(c) {
        // Nodes aligned to ONE θ — hold position and everything passes. The release.
        const node = ring(0.25);
        const kinds = ['SineRibbon', 'TriangleFence', 'StandingWave'] as const;
        c.emit(kinds[c.beat % 3]!, node, 0.95, { nodeScale: 1.4 });
      },
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// 2. Ripple Cascade — Sector 2, Neon Cascade (128 BPM)
// Teaches radial thinking after a sector of angular thinking.
// Fairness: PulseRing's node is the whole inner annulus — the answer is always "be near the axis".
// The SquareGate phase alternates on/off beats so the two never demand contradictory positions.
// ─────────────────────────────────────────────────────────────────────────────
export const RIPPLE_CASCADE: BossDef = {
  id: 'ripple-cascade',
  name: 'RIPPLE CASCADE',
  sector: 2,
  phases: [
    {
      name: 'First Ripple',
      bars: 8,
      intensity: 0.72,
      onBeat(c) {
        if (c.beat % 2 === 0) c.emit('PulseRing', 0, 0.5); // generous final radius
      },
    },
    {
      name: 'Interference',
      bars: 8,
      intensity: 0.82,
      onBeat(c) {
        c.emit('PulseRing', 0, c.beat % 2 === 0 ? 0.6 : 0.78);
      },
    },
    {
      name: 'Gate and Ring',
      bars: 8,
      intensity: 0.9,
      onBeat(c) {
        // Downbeat gate, off-beat ring — never in the same sub-step.
        if (c.beatInBar === 0) c.emit('SquareGate', ring(0.25 + dsin(c.bar) * 0.2), 0.9, { nodeScale: 1.4 });
        else if (c.beatInBar % 2 === 1) c.emit('PulseRing', 0, 0.72);
      },
    },
    {
      name: 'Cascade',
      bars: 12,
      intensity: 0.97,
      onBeat(c) {
        // Rings stepping inward; the tightest still leaves > 2× MIN_NODE_WIDTH (FS-06 §5.2 floor).
        c.emit('PulseRing', 0, 0.6 + c.phaseProgress * 0.35);
        c.emit('PulseRing', 0, 0.55 + c.phaseProgress * 0.35);
      },
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// 3. Mandelbrot Maw — Sector 3, Fractal Deep (140 BPM)
// Self-similar and recursive: the first boss that is genuinely hard.
// Fairness: FeedbackLoop's five echoes share ONE node, so it always looks harder than it is.
// The spec's own arithmetic found phase 3's 90°-apart nodes 0.11 s short of reachable, so — as it
// prescribes — the nodes start 90° apart and CONVERGE to 60° by the last bar; the convergence is
// the telegraph. Phase 4's single shared node is the release.
// ─────────────────────────────────────────────────────────────────────────────
export const MANDELBROT_MAW: BossDef = {
  id: 'mandelbrot-maw',
  name: 'MANDELBROT MAW',
  sector: 3,
  phases: [
    {
      name: 'Seed',
      bars: 8,
      intensity: 0.75,
      onBeat(c) {
        if (c.beatInBar === 0) c.emit('FeedbackLoop', ring(0.25), 0.75, { nodeScale: 1.7 });
      },
    },
    {
      name: 'Iterate',
      bars: 8,
      intensity: 0.85,
      onBeat(c) {
        if (c.beatInBar === 0) c.emit('FeedbackLoop', ring(0.25 + c.bar * 0.05), 0.85, { nodeScale: 1.5 });
        else if (c.beatInBar === 2) c.emit('SawtoothComb', ring(0.25 - c.bar * 0.05), 0.85, { nodeScale: 1.4 });
      },
    },
    {
      name: 'Boundary',
      bars: 12,
      intensity: 0.93,
      onBeat(c) {
        if (c.beatInBar !== 0) return;
        // 90° apart at the start, converging to 60° — the convergence IS the telegraph.
        const sep = (0.25 - 0.083 * c.phaseProgress) * TAU;
        const base = ring(0.25);
        c.emit('FeedbackLoop', base, 0.93, { nodeScale: 1.5 });
        if (c.bar % 2 === 1) c.emit('FeedbackLoop', base + sep, 0.93, { nodeScale: 1.5 });
      },
    },
    {
      name: 'Set',
      bars: 12,
      intensity: 1.0,
      onBeat(c) {
        // All three kinds sharing ONE node.
        const node = ring(0.25);
        if (c.beatInBar === 0) c.emit('FeedbackLoop', node, 1.0, { nodeScale: 1.5 });
        else if (c.beatInBar === 1) c.emit('SawtoothComb', node, 1.0, { nodeScale: 1.5 });
        else if (c.beatInBar === 3) c.emit('SineRibbon', node, 1.0, { nodeScale: 1.5 });
      },
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// 4. The Jammer — Sector 4, Static Shore (92 BPM)
// About information rather than reflex: it jams the HUD.
// Fairness: the jam is COSMETIC ONLY — Resonance, multiplier and power-up state are unaffected and
// stay readable. NoiseCloud's node is carved, never stochastic (FS-06 R27). Phase 4 removes the
// jamming entirely, so the hardest hazards arrive with a clean HUD.
// ─────────────────────────────────────────────────────────────────────────────
export const JAMMER: BossDef = {
  id: 'jammer',
  name: 'THE JAMMER',
  sector: 4,
  phases: [
    {
      name: 'Carrier',
      bars: 8,
      intensity: 0.7,
      onBeat(c) {
        if (c.beatInBar === 0) c.emit('NoiseCloud', ring(0.25), 0.55, { nodeScale: 1.6 });
      },
    },
    {
      name: 'Interference',
      bars: 8,
      intensity: 0.8,
      hudJam: true,
      onBeat(c) {
        if (c.beatInBar === 0) c.emit('NoiseCloud', ring(0.25 + dsin(c.bar) * 0.15), 0.7, { nodeScale: 1.5 });
        else if (c.beatInBar === 2) c.emit('SquareGate', ring(0.25 + dsin(c.bar) * 0.15), 0.8, { nodeScale: 1.4 });
      },
    },
    {
      name: 'Signal Loss',
      bars: 8,
      intensity: 0.88,
      hudJam: true,
      onBeat(c) {
        // Node glyph flickers but NEVER hides (see hazard-view: duty cycle 0.7 at 2.5 Hz).
        if (c.beatInBar % 2 === 0) c.emit('NoiseCloud', ring(0.25 + dsin(c.beat * 0.5) * 0.2), 0.88, { nodeScale: 1.45 });
      },
    },
    {
      name: 'Clear Channel',
      bars: 8,
      intensity: 0.94,
      hudJam: false,
      onBeat(c) {
        const node = ring(0.25 + dsin(c.beat * 0.4) * 0.2);
        c.emit(c.beat % 2 === 0 ? 'NoiseCloud' : 'TriangleFence', node, 0.94, { nodeScale: 1.4 });
      },
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// 5. Kick Drum Leviathan — Sector 5, Chrome Reef (150 BPM)
// The most rhythmic boss and the purest expression of Pillar P1.
// Fairness: SubBassSlab is one node in a filled disc — a pure "right place at the right time" test.
// The spec found 120° of arc takes 1.05 s versus a 0.4 s beat, so phase 3's slabs alternate on
// ALTERNATE BARS, not beats, giving 1.6 s. BeatGate is open at arrival by construction (FS-06 §4.10),
// so the gate can never be the killer.
// ─────────────────────────────────────────────────────────────────────────────
export const KICK_DRUM_LEVIATHAN: BossDef = {
  id: 'kick-drum-leviathan',
  name: 'KICK DRUM LEVIATHAN',
  sector: 5,
  phases: [
    {
      name: 'Four on the Floor',
      bars: 8,
      intensity: 0.76,
      onBeat(c) {
        if (c.beatInBar === 0) c.emit('SubBassSlab', ring(0.25), 0.76, { nodeScale: 1.9 });
      },
    },
    {
      name: 'Gated',
      bars: 8,
      intensity: 0.86,
      onBeat(c) {
        // Slabs on 1 and 3, gates on 2 and 4.
        if (c.beatInBar === 0 || c.beatInBar === 2) c.emit('SubBassSlab', ring(0.25 + dsin(c.bar) * 0.15), 0.86, { nodeScale: 1.8 });
        else c.emit('BeatGate', ring(0.25), 0.86, { nodeScale: 1.5 });
      },
    },
    {
      name: 'Double Time',
      bars: 8,
      intensity: 0.93,
      onBeat(c) {
        // Alternating θ by 120°, but on ALTERNATE BARS — 1.6 s to cross 22 u of arc.
        if (c.beatInBar !== 0) return;
        const third = c.bar % 3;
        c.emit('SubBassSlab', ring(0.25 + third / 3), 0.93, { nodeScale: 1.8 });
      },
    },
    {
      name: 'Leviathan',
      bars: 12,
      intensity: 1.0,
      onBeat(c) {
        if (c.beatInBar === 0) c.emit('SubBassSlab', ring(0.25 + (c.bar % 2) * 0.25), 1.0, { nodeScale: 1.8 });
        else if (c.beatInBar === 2) c.emit('BeatGate', ring(0.25), 1.0, { nodeScale: 1.5 });
      },
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// 6. The Silent Node — Sector 6, Aurora Null (174 BPM)
// The music STOPS. Proves no-audio parity is real rather than claimed.
// Fairness: the accessibility floor already guarantees a full visual beat indicator and no mechanic
// requiring hearing; phase 3 simply makes every player use it. A player with sound already off
// experiences no change — the design intent. Phase 3 caps HelixBraid's sweep rate.
// ─────────────────────────────────────────────────────────────────────────────
export const SILENT_NODE: BossDef = {
  id: 'silent-node',
  name: 'THE SILENT NODE',
  sector: 6,
  phases: [
    {
      name: 'Full Signal',
      bars: 8,
      intensity: 0.78,
      musicLevel: 1,
      onBeat(c) {
        if (c.beatInBar === 0) c.emit('HelixBraid', ring(0.25), 0.7, { nodeScale: 1.6 });
        else if (c.beatInBar === 2) c.emit('StandingWave', ring(0.25), 0.78, { nodeScale: 1.5 });
      },
    },
    {
      name: 'Fade',
      bars: 8,
      intensity: 0.86,
      musicLevel: 0.3,
      onBeat(c) {
        if (c.beatInBar % 2 === 0) c.emit('HelixBraid', ring(0.25 + dsin(c.beat * 0.5) * 0.15), 0.86, { nodeScale: 1.5 });
      },
    },
    {
      name: 'Silence',
      bars: 12,
      intensity: 0.94,
      musicLevel: 0,
      onBeat(c) {
        // rate capped at 60 % of max (intensity 0.6) — a moving node in silence is a lot.
        if (c.beatInBar === 0) c.emit('HelixBraid', ring(0.25), 0.6, { nodeScale: 1.7 });
        else if (c.beatInBar === 2) c.emit('HelixBraid', ring(0.75), 0.6, { nodeScale: 1.7 });
      },
    },
    {
      name: 'Return',
      bars: 8,
      intensity: 1.0,
      musicLevel: 1,
      onBeat(c) {
        const node = ring(0.25);
        const kinds = ['HelixBraid', 'StandingWave', 'SineRibbon', 'TriangleFence'] as const;
        c.emit(kinds[c.beat % 4]!, node, 0.9, { nodeScale: 1.5 });
      },
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// 7. Gravity Well — Sector 7, Event Horizon (100 → 200 BPM)
// Everything contracts; the tempo accelerates through the encounter. ChirpSweep is ABSENT (R19 —
// the sector reaches tier 5 and ChirpSweep's 1.5 s telegraph does not fit).
// Fairness: at 200 BPM a half-beat is 0.15 s, but PulseRing's node is radial and STATIONARY — the
// rings converge toward you rather than requiring lateral movement, so the lateral demand in phase 4
// is near zero. That is what makes 0.15 s spacing survivable.
// ─────────────────────────────────────────────────────────────────────────────
export const GRAVITY_WELL: BossDef = {
  id: 'gravity-well',
  name: 'GRAVITY WELL',
  sector: 7,
  phases: [
    {
      name: 'Approach',
      bars: 8,
      intensity: 0.8,
      bpm: 160,
      onBeat(c) {
        if (c.beat % 2 === 0) c.emit('PulseRing', 0, 0.55);
        else if (c.beatInBar === 1) c.emit('SineRibbon', ring(0.25), 0.8, { nodeScale: 1.5 });
      },
    },
    {
      name: 'Photon Sphere',
      bars: 8,
      intensity: 0.9,
      bpm: 175,
      onBeat(c) {
        if (c.beatInBar === 0) c.emit('FeedbackLoop', ring(0.25), 0.9, { nodeScale: 1.6 });
        else if (c.beatInBar === 2) c.emit('PulseRing', 0, 0.7);
      },
    },
    {
      name: 'Ergosphere',
      bars: 8,
      intensity: 0.96,
      bpm: 188,
      onBeat(c) {
        if (c.beatInBar === 0) c.emit('HelixBraid', ring(0.25), 0.8, { nodeScale: 1.6 });
        else if (c.beatInBar === 2) c.emit('FeedbackLoop', ring(0.25), 0.9, { nodeScale: 1.6 });
      },
    },
    {
      name: 'Singularity',
      bars: 12,
      intensity: 1.0,
      bpm: 200,
      onBeat(c) {
        // Radii stepping to the floor. Purely radial: the lateral demand is near zero.
        c.emit('PulseRing', 0, 0.6 + c.phaseProgress * 0.38);
        c.emit('PulseRing', 0, 0.55 + c.phaseProgress * 0.38);
      },
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// 8. The Fundamental — Sector 8 (variable BPM)
// A medley that revisits all seven previous encounters, then resolves into a single held tone and a
// single SineRibbon — the shape the game started with.
// Fairness: phases 1–4 are compressed versions of encounters already beaten, at node widths 1.15×
// the originals'. Phase 5 is DELIBERATELY trivial — the last 8 bars are one slow sine with a wide
// node. A game whose final act is a wall of unreadable hazards ends on frustration, not resolution.
// ─────────────────────────────────────────────────────────────────────────────
export const FUNDAMENTAL: BossDef = {
  id: 'fundamental',
  name: 'THE FUNDAMENTAL',
  sector: 8,
  phases: [
    {
      name: 'Recapitulation I',
      bars: 8,
      intensity: 0.85,
      bpm: 128,
      onBeat(c) {
        const node = ring(0.25 + dsin(c.beat * 0.9) * 0.16);
        if (c.beatInBar % 2 === 0) c.emit('SineRibbon', node, 0.85, { nodeScale: 1.7 });
        else c.emit('PulseRing', 0, 0.65);
      },
    },
    {
      name: 'Recapitulation II',
      bars: 8,
      intensity: 0.9,
      bpm: 140,
      onBeat(c) {
        if (c.beatInBar === 0) c.emit('FeedbackLoop', ring(0.25), 0.85, { nodeScale: 1.7 });
        else if (c.beatInBar === 2) c.emit('NoiseCloud', ring(0.25), 0.8, { nodeScale: 1.7 });
      },
    },
    {
      name: 'Recapitulation III',
      bars: 8,
      intensity: 0.95,
      bpm: 174,
      onBeat(c) {
        if (c.beatInBar === 0) c.emit('SubBassSlab', ring(0.25 + (c.bar % 2) * 0.3), 0.9, { nodeScale: 2.0 });
        else if (c.beatInBar === 2) c.emit('HelixBraid', ring(0.25), 0.7, { nodeScale: 1.7 });
      },
    },
    {
      name: 'Convergence',
      bars: 12,
      intensity: 1.0,
      bpm: 200,
      onBeat(c) {
        c.emit('PulseRing', 0, 0.6 + c.phaseProgress * 0.3);
        if (c.beatInBar === 0) c.emit('StandingWave', ring(0.25), 0.9, { nodeScale: 1.7 });
      },
    },
    {
      name: 'The Fundamental',
      bars: 8,
      intensity: 0.3,
      bpm: 118,
      onBeat(c) {
        // One slow sine, freq 1, wide node. This is the ending, not a difficulty spike.
        if (c.beatInBar === 0 && c.bar % 2 === 0) c.emit('SineRibbon', ring(0.25), 0.15, { nodeScale: 2.4 });
      },
    },
  ],
};

export const BOSSES: Record<string, BossDef> = {
  oscillator: OSCILLATOR,
  'ripple-cascade': RIPPLE_CASCADE,
  'mandelbrot-maw': MANDELBROT_MAW,
  jammer: JAMMER,
  'kick-drum-leviathan': KICK_DRUM_LEVIATHAN,
  'silent-node': SILENT_NODE,
  'gravity-well': GRAVITY_WELL,
  fundamental: FUNDAMENTAL,
};
